import type { Slide } from '../services/lectureApi';

/**
 * "Advance-when-covered" gating — the single definition.
 *
 * A slide is reachable once every EARLIER slide whose `advance_rule` demands it
 * has been covered (narration finished, or its widget completed). Slides at or
 * behind the current position, and anything already covered, stay open for free
 * review. Admins and super-admins bypass gating entirely so they can review
 * content without sitting through it.
 *
 * WHY THIS IS A MODULE AND NOT A COMPONENT HELPER: it used to live inside
 * SlideProgressRail, which meant the rail was the ONLY thing that enforced it.
 * The rail correctly drew a padlock and refused the click — and then the Next
 * button in the transport, which calls the store directly, walked straight past
 * every lock. The instructor found it: "lesson in left list are locked which is
 * great forcing student to learn and progress but clicking on Next and Previous
 * beside play button auto unlocks."
 *
 * A rule enforced by one of several callers is not a rule. The store's
 * navigation actions now call this too, so the gate holds no matter which
 * control a student uses — the rail, the transport, or a keyboard shortcut.
 */
export interface SlideLockContext {
  slides: Pick<Slide, 'id' | 'advance_rule'>[];
  completedSlides: string[];
  currentSlideIndex: number;
  isAdmin?: boolean;
}

/** A slide whose `advance_rule` makes covering it a precondition for later slides. */
export function isGated(slide: Pick<Slide, 'advance_rule'> | undefined): boolean {
  const rule = slide?.advance_rule ?? 'manual';
  return rule === 'on_audio_end' || rule === 'on_widget';
}

export function isCovered(ctx: SlideLockContext, index: number): boolean {
  const slide = ctx.slides[index];
  return slide ? ctx.completedSlides.includes(slide.id) : false;
}

/** Is slide `index` locked for this learner right now? */
export function isSlideLocked(ctx: SlideLockContext, index: number): boolean {
  if (ctx.isAdmin) return false;
  if (index <= ctx.currentSlideIndex) return false;
  if (isCovered(ctx, index)) return false;
  for (let j = 0; j < index; j++) {
    if (isGated(ctx.slides[j]) && !isCovered(ctx, j)) return true;
  }
  return false;
}

/**
 * The furthest index a learner may move to right now.
 *
 * Used by the transport: Next must stop at the gate rather than step over it,
 * and the button can be disabled rather than silently doing nothing.
 */
export function maxReachableIndex(ctx: SlideLockContext): number {
  if (ctx.isAdmin) return Math.max(0, ctx.slides.length - 1);
  for (let i = ctx.currentSlideIndex + 1; i < ctx.slides.length; i++) {
    if (isSlideLocked(ctx, i)) return i - 1;
  }
  return Math.max(0, ctx.slides.length - 1);
}
